import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import dayjs from "dayjs";
import { business_integrations, ILocation, IValidationResult, patients } from "lobbie";
import Account from "src/classes/Account";
import { PAYMENT_PROCESSOR_NOT_SETUP } from "src/components/pages/staff/payments/constants";
import { BROWSER_TIME_ZONE, DEFAULT_ERROR_MESSAGE } from "src/constants";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { RequestError } from "src/hooks/useAxios";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import type { RootState } from "src/redux/store";
import {
    ECareResourceType,
    ECollectionOption,
    EPaymentRequestStatus,
    ESubscriptionRecurrenceFrequencyUnit,
    ESubscriptionRecurrenceType,
} from "src/types/global_enums";
import { handleError, isFailedRequest, notify } from "src/utils";

export interface InvoiceCharge {
    id: string;
    careResourceId: number;
    existingInvoiceLineItemId?: number;
    code: string;
    description: string;
    qty: number;
    cost: number | null;
    isService: boolean;
    careResourceType: ECareResourceType;
    editablePrice?: number;
    discount?: number;
    recurrenceFrequencyUnit?: ESubscriptionRecurrenceFrequencyUnit;
    recurrenceFrequencyValue?: number;
    occurrences?: number;
    isBillableToInsurance?: boolean;
    taxRate?: number;
    startDateUnix?: number;
    serviceRecurrenceType?: ESubscriptionRecurrenceType;
    isPrepopulatedFromApptForms: boolean;
}

export interface IInvoiceDrawerState {
    // Original properties
    show: boolean;
    appointmentId?: number;
    invoiceId?: number;
    onSaveDraft?: () => void;
    onCheckout?: () => void;
    // Loading and error states
    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;
    // Invoice data
    invoiceDetails?: business_integrations.IInvoiceDetails;
    invoiceBuildingContext?: business_integrations.IInvoiceBuildingContext;
    chargableResources?: business_integrations.IChargableInvoiceResources;
    materialResourceChargeCatalog: InvoiceCharge[];
    serviceChargeCatalog: InvoiceCharge[];
    wholeCatalog: InvoiceCharge[];
    // Invoice state tracking
    isEditMode: boolean;
    isReadOnly: boolean;
    isVoid: boolean;
    isRefunded: boolean;
    invoiceDetailsFetched: boolean;
    chargeableResourcesFetched: boolean;
    existingInvoiceId: number | null;
    // Selected data
    selectedPatient?: patients.IAccountPatient;
    selectedLocation?: ILocation;
    selectedServices: InvoiceCharge[];
    selectedMaterialResource: InvoiceCharge[];
    // Payment related
    collectionOption: ECollectionOption;
    selectedCardId: number | null;
    selectedTerminalId: string | null;
    showPaymentMethodConfigTab: boolean;
    // Location related
    isLocationEditable: boolean;
}

export const initialStateInvoiceDrawer: IInvoiceDrawerState = {
    show: false,
    invoiceId: undefined,
    appointmentId: undefined,
    onCheckout: undefined,
    onSaveDraft: undefined,

    isLoading: false,
    isSubmitting: false,
    error: null,

    invoiceDetails: undefined,
    invoiceBuildingContext: undefined,
    chargableResources: undefined,
    materialResourceChargeCatalog: [],
    serviceChargeCatalog: [],
    wholeCatalog: [],

    isEditMode: false,
    isReadOnly: true,
    isVoid: false,
    isRefunded: false,
    invoiceDetailsFetched: false,
    chargeableResourcesFetched: false,
    existingInvoiceId: null,

    selectedPatient: undefined,
    selectedLocation: undefined,
    selectedServices: [],
    selectedMaterialResource: [],

    collectionOption: ECollectionOption.SEND_TO_READER,
    selectedCardId: null,
    selectedTerminalId: null,
    showPaymentMethodConfigTab: false,

    isLocationEditable: true,
};

const notifyIfPaymentVendorIsNotSetup = (error: unknown): boolean => {
    if ((error as RequestError).httpError.userAlertTitle === PAYMENT_PROCESSOR_NOT_SETUP) {
        notify({
            level: "error",
            title: "Payment Vendor is not setup for this location",
            message: "Please setup payment vendor for this location",
            duration: 10000,
        });
        return false;
    }
    return true;
};

export const selectHasInvoiceChanges = (state: RootState): boolean => {
    const { invoiceDetails, selectedServices, selectedMaterialResource } = state.invoiceDrawer;
    if (!invoiceDetails) return true;
    const originalLineItems = invoiceDetails.lineItems;
    const currentLineItems = [...selectedServices, ...selectedMaterialResource];
    // Check if any items were added or removed
    if (originalLineItems.length !== currentLineItems.length) return true;
    // Check for changes in existing items
    return currentLineItems.some((current) => {
        const original = originalLineItems.find((o) => o.careResourceId === current.careResourceId);
        if (!original) return true;
        return (
            current.qty !== original.quantity ||
            (current.discount !== undefined && current.discount !== original.discountPercent) ||
            (current.editablePrice &&
                Math.round(current.editablePrice * 100) !==
                    original.amountCents + (original.priceAdjustmentCents || 0))
        );
    });
};

export const selectInvoiceTotals = (state: RootState) => {
    const { selectedServices, selectedMaterialResource } = state.invoiceDrawer;
    const charges = [...selectedServices, ...selectedMaterialResource];

    const oneTimeTotal = charges
        .filter((charge) => !charge.serviceRecurrenceType)
        .reduce((sum, item) => {
            // Get base price - either edited price or original cost
            const basePrice = item.editablePrice ?? item.cost ?? 0;
            // Apply discount if exists
            const priceAfterDiscount = basePrice * (1 - (item.discount || 0) / 100);
            // Multiply by quantity
            return sum + item.qty * priceAfterDiscount;
        }, 0);

    const recurringTotal = charges
        .filter((charge) => charge.serviceRecurrenceType)
        .reduce((sum, item) => {
            // Get base price - either edited price or original cost
            const basePrice = item.editablePrice ?? item.cost ?? 0;
            // Apply discount if exists
            const priceAfterDiscount = basePrice * (1 - (item.discount || 0) / 100);
            return sum + priceAfterDiscount;
        }, 0);

    return {
        oneTimeTotal,
        recurringTotal,
        hasRecurring: charges.some((charge) => charge.serviceRecurrenceType),
    };
};

export const selectIsInvoiceValid = (state: RootState) => {
    const { selectedServices, selectedMaterialResource } = state.invoiceDrawer;
    return selectedServices.length > 0 || selectedMaterialResource.length > 0;
};

export const selectHasSubscriptions = (state: RootState) => {
    const { selectedServices } = state.invoiceDrawer;
    return selectedServices.some((service) => service.serviceRecurrenceType !== undefined);
};
const hasSubscriptions = (slice: IInvoiceDrawerState) => {
    return slice.selectedServices.some((service) => service.serviceRecurrenceType !== undefined);
};

export const fetchInvoiceDetails = createAsyncThunk<
    business_integrations.IInvoiceDetails,
    number | undefined,
    { rejectValue: string }
>("invoiceDrawer/fetchInvoiceDetails", async (invoiceId, { rejectWithValue }) => {
    try {
        if (!invoiceId) return rejectWithValue("No invoice ID provided");
        const response = await axios.get<business_integrations.IInvoiceDetails>(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/revenue-sentinel/invoices/${invoiceId}`,
            { withCredentials: true },
        );
        return response.data;
    } catch (error: any) {
        notifyIfPaymentVendorIsNotSetup(error);
        handleBackendHttpError(error);
        return rejectWithValue("Failed to fetch invoice details");
    }
});

export const fetchAppointmentInvoiceContext = createAsyncThunk<
    business_integrations.IInvoiceDetails | business_integrations.IInvoiceBuildingContext,
    number | undefined,
    { rejectValue: string }
>("invoiceDrawer/fetchAppointmentInvoiceContext", async (appointmentId, { rejectWithValue }) => {
    try {
        if (!appointmentId) return rejectWithValue("No appointment ID provided");
        // this might return either a building context or an invoice details object as an appointment can have an invoice already linked to it
        const response = await axios.get<
            business_integrations.IInvoiceDetails | business_integrations.IInvoiceBuildingContext
        >(`${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/invoice`, {
            withCredentials: true,
        });
        return response.data;
    } catch (error: any) {
        notifyIfPaymentVendorIsNotSetup(error);
        handleBackendHttpError(error);
        return rejectWithValue("Failed to fetch appointment invoice context");
    }
});

export const fetchChargableResources = createAsyncThunk<
    business_integrations.IChargableInvoiceResources,
    void,
    { rejectValue: string }
>("invoiceDrawer/fetchChargableResources", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<business_integrations.IChargableInvoiceResources>(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/revenue-sentinel/chargable-invoice-resources?active=true`,
            { withCredentials: true },
        );
        return response.data;
    } catch (error: any) {
        notifyIfPaymentVendorIsNotSetup(error);
        handleBackendHttpError(error);
        return rejectWithValue("Failed to fetch chargable resources");
    }
});

/*
 * This thunk is used to load the invoice drawer with the necessary data to display the invoice.
 * It is used in both cases of displaying the InvoiceDrawer and displaying of the embedded InvoiceDrawerContent in another drawer hence the show parameter.
 */
export const loadInvoiceDrawer = createAsyncThunk<
    void,
    {
        show?: boolean;
        invoiceId?: number;
        appointmentId?: number;
        accountPatientId?: number;
        onSaveDraft?: () => void;
        onCheckout?: () => void;
    },
    { state: RootState; rejectValue: string }
>("invoiceDrawer/loadInvoiceDrawer", async (params, { dispatch, getState, rejectWithValue }) => {
    try {
        const state = getState();
        const slice = state.invoiceDrawer;
        const account = new Account(state.account.account as Account);
        const { lowerPatientFieldName } = account;

        // First, try to fetch invoice details if we have an ID
        let invoiceDetails = undefined;
        if (params.invoiceId) {
            invoiceDetails = await dispatch(fetchInvoiceDetails(params.invoiceId)).unwrap();
            // Check if we need to fetch chargeable resources
            if (
                invoiceDetails?.isDraft ||
                !invoiceDetails?.statuses.includes(EPaymentRequestStatus.PAID)
            ) {
                await dispatch(fetchChargableResources()).unwrap();
            }
        } else if (params.appointmentId) {
            invoiceDetails = await dispatch(
                fetchAppointmentInvoiceContext(params.appointmentId),
            ).unwrap();
            await dispatch(fetchChargableResources()).unwrap();
        } else {
            // No IDs provided, just fetch chargeable resources for a new invoice
            await dispatch(fetchChargableResources()).unwrap();
            // Fetch patient data if accountPatientId is provided
            if (params.accountPatientId) {
                try {
                    const response = await axios.get<IValidationResult | patients.IAccountPatient>(
                        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/patients/${params.accountPatientId}`,
                        { withCredentials: true },
                    );
                    const result = response && response.data;
                    if (!result) {
                        if (response?.status && response.status > 399) {
                            notify({
                                level: "error",
                                title: `Error getting ${lowerPatientFieldName}.`,
                                message: DEFAULT_ERROR_MESSAGE,
                            });
                        }
                    } else if (isFailedRequest(result)) {
                        notify({
                            level: "error",
                            title: `Failed to get ${lowerPatientFieldName}.`,
                            message: (result as IValidationResult).message || DEFAULT_ERROR_MESSAGE,
                        });
                    } else {
                        // Set the patient in the slice
                        dispatch(
                            invoiceDrawerSlice.actions.setSelectedPatient(
                                result as patients.IAccountPatient,
                            ),
                        );
                    }
                } catch (error) {
                    handleError(error as Error);
                    notify({
                        level: "error",
                        title: `Error getting ${lowerPatientFieldName}`,
                        message: DEFAULT_ERROR_MESSAGE,
                    });
                }
            }
        }

        // If there's an appointment ID or the invoice is linked to an appointment
        const allLocations = state.location.locations;
        if (params.appointmentId) {
            dispatch(invoiceDrawerSlice.actions.setIsLocationEditable(false));
            dispatch(
                invoiceDrawerSlice.actions.setSelectedLocation(
                    allLocations?.find((l) => l.id === invoiceDetails?.locationId),
                ),
            );
            return;
        }

        if (invoiceDetails) {
            dispatch(invoiceDrawerSlice.actions.setIsLocationEditable(false));
            dispatch(
                invoiceDrawerSlice.actions.setSelectedLocation(
                    allLocations?.find((l) => l.id === invoiceDetails?.locationId),
                ),
            );
            return;
        }

        const currentGlobalLocationId = state.location.locationId;
        // For new invoices that aren't associated with appointments
        if (!slice.appointmentId) {
            // Check if current location has payment setup
            if (
                currentGlobalLocationId &&
                !account.isLocationPaymentSetup(currentGlobalLocationId)
            ) {
                // Try to find a location with payment setup
                for (const locationId of Object.keys(account.paymentProcessorAccounts)) {
                    const locId = parseInt(locationId);
                    if (account.isLocationPaymentSetup(locId)) {
                        const location_ = allLocations?.find((l) => l.id === locId);
                        dispatch(invoiceDrawerSlice.actions.setSelectedLocation(location_));
                        return;
                    }
                }
            } else if (currentGlobalLocationId) {
                const location_ = allLocations?.find((l) => l.id === currentGlobalLocationId);
                dispatch(invoiceDrawerSlice.actions.setSelectedLocation(location_));
            }
        }
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue("Failed to open invoice drawer with data");
    }
});

export const submitInvoice = createAsyncThunk<
    { invoice?: business_integrations.IInvoiceDetails; showPaymentMethodConfigTab?: boolean },
    { isDraft: boolean; forcedCollectionOption?: ECollectionOption },
    { state: RootState; rejectValue: string }
>("invoiceDrawer/submitInvoice", async (params, { dispatch, getState, rejectWithValue }) => {
    const slice = getState().invoiceDrawer;
    const effectiveCollectionOption = params.forcedCollectionOption || slice.collectionOption;
    if (
        !params.isDraft &&
        hasSubscriptions(slice) &&
        effectiveCollectionOption !== ECollectionOption.CHARGE_CREDIT_CARD
    ) {
        notify({
            level: "warning",
            title: "Payment Method Required",
            message: "Subscriptions require choosing saved credit card.",
        });
        return rejectWithValue("Subscriptions require choosing saved credit card.");
    }
    if (
        !params.isDraft &&
        ((effectiveCollectionOption === ECollectionOption.CHARGE_CREDIT_CARD &&
            slice.selectedCardId === null) ||
            (effectiveCollectionOption === ECollectionOption.SEND_TO_READER &&
                slice.selectedTerminalId === null))
    ) {
        return {
            invoice: undefined,
            showPaymentMethodConfigTab: true,
        };
    }
    try {
        if (!slice.selectedLocation && !slice.appointmentId) {
            notify({
                level: "warning",
                title: "No Location Selected",
                message: "Please select a location",
            });
            return rejectWithValue("No location selected");
        }
        if (!slice.appointmentId && !slice.selectedPatient) {
            notify({
                level: "warning",
                title: "No Patient Selected",
                message: "Please select a patient",
            });
            return rejectWithValue("No patient selected");
        }
        if (slice.selectedServices.length === 0 && slice.selectedMaterialResource.length === 0) {
            notify({
                level: "warning",
                title: "No items to invoice",
                message: "Please add at least one item to the invoice",
            });
            return rejectWithValue("No items selected");
        }
        // Build line items from services and material resources
        const lineItems: business_integrations.InvoiceLineItem[] = [
            ...slice.selectedServices.map((service) => ({
                ...(service.existingInvoiceLineItemId && {
                    existingInvoiceLineItemId: service.existingInvoiceLineItemId,
                }),
                careResourceId: service.careResourceId,
                careResourceType: service.careResourceType,
                quantity: service.qty,
                defaultChargeAmountCents: Math.round((service.cost ?? 0) * 100),
                ...(service.discount && {
                    appliedDiscountPercent: service.discount,
                }),
                ...(service.editablePrice && {
                    appliedPriceAdjustmentCents: Math.round(
                        (service.editablePrice - (service.cost ?? 0)) * 100,
                    ),
                }),
                ...(service.startDateUnix && { startDateUnix: service.startDateUnix }),
            })),
            ...slice.selectedMaterialResource.map((item) => ({
                ...(item.existingInvoiceLineItemId && {
                    existingInvoiceLineItemId: item.existingInvoiceLineItemId,
                }),
                careResourceId: item.careResourceId,
                careResourceType: item.careResourceType,
                quantity: item.qty,
                defaultChargeAmountCents: item.cost ? Math.round(item.cost * 100) : 0,
                ...(item.editablePrice && {
                    appliedPriceAdjustmentCents: Math.round(
                        (item.editablePrice - (item.cost ?? 0)) * 100,
                    ),
                }),
                appliedDiscountPercent: item.discount || 0,
            })),
        ];
        // Prepare the payload
        const payload: business_integrations.IBuildInvoicePayload = {
            ...(slice.existingInvoiceId && { invoiceId: slice.existingInvoiceId }),
            isDraft: params.isDraft,
            ...(slice.appointmentId && { appointmentId: slice.appointmentId }),
            ...(!slice.appointmentId && {
                locationId: slice.selectedLocation?.id,
            }),
            paymentIntent: {
                currency: "USD",
                description: "",
                paymentMethodId: slice.selectedCardId ?? undefined,
                shouldAutoChargePatient:
                    slice.collectionOption === ECollectionOption.CHARGE_CREDIT_CARD &&
                    !params.isDraft,
                shouldSendToPaymentTerminal:
                    slice.collectionOption === ECollectionOption.SEND_TO_READER && !params.isDraft,
                shouldEmailPatient:
                    slice.collectionOption === ECollectionOption.SEND_INVOICE && !params.isDraft,
                accountPatientId:
                    slice.selectedPatient?.accountPatientId ??
                    slice.invoiceDetails?.accountPatient.id ??
                    0,
                externalPaymentTerminalId: slice.selectedTerminalId ?? undefined,
            },
            lineItems,
        };
        const locationId =
            slice.selectedLocation?.id ||
            slice.invoiceDetails?.locationId ||
            slice.invoiceBuildingContext?.locationId;
        if (!locationId) {
            return rejectWithValue("No location ID available");
        }
        // Submit the invoice
        const response = await axios.post<business_integrations.IInvoiceDetails>(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/revenue-sentinel/invoices/build?locationId=${locationId}`,
            payload,
            { withCredentials: true },
        );
        // Show appropriate notifications based on response
        if (params.isDraft) {
            notify({
                level: "success",
                title: "Success",
                message: "Invoice draft saved successfully",
            });
        } else {
            // Determine what was created (invoice, subscription or both)
            const hasOneTimeCharges = [
                ...slice.selectedServices,
                ...slice.selectedMaterialResource,
            ].some((charge) => !charge.serviceRecurrenceType && charge.cost !== null);
            const numSubscriptions = slice.selectedServices.filter(
                (service) => service.serviceRecurrenceType,
            ).length;
            let message = "";
            if (hasOneTimeCharges && numSubscriptions) {
                message = `Invoice and subscription${
                    numSubscriptions > 1 ? "s" : ""
                } created successfully`;
            } else if (hasOneTimeCharges) {
                message = "Invoice created successfully";
            } else if (numSubscriptions) {
                message = `Subscription${numSubscriptions > 1 ? "s" : ""} created successfully`;
            }
            notify({
                level: "success",
                title: "Success",
                message,
            });
        }
        if (!params.isDraft) {
            await dispatch(fetchInvoiceDetails(response.data.id));
        }
        dispatch(setSelectedCardId(null));
        return {
            invoice: response.data,
            showPaymentMethodConfigTab: false,
        };
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue("Failed to submit invoice");
    }
});

export const retryPayment = createAsyncThunk<void, void, { state: RootState; rejectValue: string }>(
    "invoiceDrawer/retryPayment",
    async (_, { dispatch, getState, rejectWithValue }) => {
        try {
            const state = getState().invoiceDrawer;
            if (!state.invoiceDetails?.id) {
                return rejectWithValue("No invoice ID available");
            }
            const payload = {
                accountPatientId: state.invoiceDetails.accountPatient.id,
                currency: "USD",
                description: "",
                shouldEmailPatient: state.collectionOption === ECollectionOption.SEND_INVOICE,
                shouldAutoChargePatient:
                    state.collectionOption === ECollectionOption.CHARGE_CREDIT_CARD,
                shouldSendToPaymentTerminal:
                    state.collectionOption === ECollectionOption.SEND_TO_READER,
                paymentMethodId: state.selectedCardId ?? null,
                externalPaymentTerminalId: state.selectedTerminalId || null,
                // RetryPayment specific fields
                // TODO: PAYMENTS_2 add frontend end logic to allow Staff to specify a payment method replacement for subscriptions or set a default payment method
                makeDefault: false,
                updateSubscriptionPaymentMethods: false,
            };
            const response = await axios.post(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/revenue-sentinel/invoices/${state.invoiceDetails.id}/retry-payment`,
                payload,
                { withCredentials: true },
            );
            // Show notification based on payment status
            if (response.data === EPaymentRequestStatus.PAID) {
                notify({
                    level: "success",
                    title: "Success",
                    message: "Payment processed successfully",
                });
            } else if (response.data === EPaymentRequestStatus.FAILED) {
                notify({
                    level: "error",
                    title: "Payment Failed",
                    message: "Payment was not processed",
                });
            }
            await dispatch(fetchInvoiceDetails(state.invoiceDetails.id));
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue("Failed to retry payment");
        }
    },
);

export const refundInvoice = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: string }
>("invoiceDrawer/refundInvoice", async (_, { dispatch, getState, rejectWithValue }) => {
    try {
        const state = getState().invoiceDrawer;
        if (!state.invoiceDetails?.id) {
            return rejectWithValue("No invoice ID available");
        }
        await axios.post(
            `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/revenue-sentinel/invoices/${state.invoiceDetails.id}/refund`,
            {},
            { withCredentials: true },
        );
        notify({
            level: "success",
            title: "Refund Successful",
            message: "The invoice has been refunded.",
        });
        await dispatch(fetchInvoiceDetails(state.invoiceDetails.id));
    } catch (error: any) {
        handleBackendHttpError(error);
        return rejectWithValue("Failed to refund invoice");
    }
});

export const selectLocation = createAsyncThunk<
    void,
    {
        location: ILocation | undefined;
    },
    { state: RootState }
>("invoiceDrawer/selectLocation", (params, { dispatch, getState }) => {
    const { location } = params;
    const state = getState();
    const account = new Account(state.account.account as Account);
    // If no location is selected, just update state
    if (!location) {
        dispatch(invoiceDrawerSlice.actions.setSelectedLocation(undefined));
        return;
    }
    // Check if the location has payment processing set up
    const isPaymentProcessorSetUp = account.isPaymentSetupRequired();
    const isSetup = account.isLocationPaymentSetup(location.id);
    // Show warning notification if location doesn't have payment processing set up
    if (!isSetup && isPaymentProcessorSetUp) {
        notify({
            level: "warning",
            title: `Payment Configuration for ${location.name} is not setup`,
            message: "Please setup payment configuration for this location before proceeding",
        });
    }
    // Update the selected location in the state
    dispatch(invoiceDrawerSlice.actions.setSelectedLocation(location));
});

const invoiceDrawerSlice = createSlice({
    name: "invoiceDrawer",
    initialState: initialStateInvoiceDrawer,
    reducers: {
        hideInvoiceDrawer: () => initialStateInvoiceDrawer,
        // Patient/location selection
        setSelectedPatient: (
            state,
            action: PayloadAction<patients.IAccountPatient | undefined>,
        ) => {
            state.selectedPatient = action.payload;
        },
        setSelectedLocation: (state, action: PayloadAction<ILocation | undefined>) => {
            state.selectedLocation = action.payload;
        },
        setIsLocationEditable: (state, action: PayloadAction<boolean>) => {
            state.isLocationEditable = action.payload;
        },
        // Payment related actions
        setCollectionOption: (state, action: PayloadAction<ECollectionOption>) => {
            state.collectionOption = action.payload;
        },
        setSelectedCardId: (state, action: PayloadAction<number | null>) => {
            state.selectedCardId = action.payload;
        },
        setSelectedTerminalId: (state, action: PayloadAction<string | null>) => {
            state.selectedTerminalId = action.payload;
        },
        setShowPaymentMethodConfigTab: (state, action: PayloadAction<boolean>) => {
            state.showPaymentMethodConfigTab = action.payload;
        },
        addInvoiceLineItem: (state, action: PayloadAction<InvoiceCharge>) => {
            const item = action.payload;
            if (item.isService) {
                const chargeWithStartDate = {
                    ...action.payload,
                    startDateUnix: action.payload.serviceRecurrenceType
                        ? dayjs().tz(BROWSER_TIME_ZONE).startOf("day").valueOf()
                        : action.payload.startDateUnix,
                };
                state.selectedServices.push(chargeWithStartDate);
            } else {
                state.selectedMaterialResource.push(item);
            }
        },
        removeInvoiceLineItem: (state, action: PayloadAction<InvoiceCharge>) => {
            const item = action.payload;
            if (item.isService) {
                state.selectedServices = state.selectedServices.filter(
                    (service) => service.id !== item.id,
                );
            } else {
                state.selectedMaterialResource = state.selectedMaterialResource.filter(
                    (material) => material.id !== item.id,
                );
            }
        },
        updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
            const item_ = state.wholeCatalog?.find((i) => i.id === action.payload.id);
            const items = item_?.isService
                ? state.selectedServices
                : state.selectedMaterialResource;
            const item = items.find((i) => i.id === action.payload.id);
            if (item) {
                item.qty = action.payload.quantity;
            }
        },
        // Price and discount management
        applyDiscount: (state, action: PayloadAction<{ id: string; discount: number }>) => {
            const updateDiscount = (items: InvoiceCharge[]) => {
                const item = items.find((i) => i.id === action.payload.id);
                if (item) {
                    if (action.payload.discount >= 100) {
                        notify({
                            level: "warning",
                            title: "Invalid Discount",
                            message: "Discount cannot be greater than or equal to 100%",
                        });
                        return;
                    }
                    if (action.payload.discount < 0) {
                        notify({
                            level: "warning",
                            title: "Invalid Discount",
                            message: "Discount cannot be less than 0%",
                        });
                        return;
                    }
                    const discountAmount = item.cost
                        ? (item.cost * action.payload.discount) / 100
                        : 0;
                    if (item.cost && discountAmount > item.cost) {
                        notify({
                            level: "warning",
                            title: "Invalid Discount",
                            message: "Discount cannot be greater than the cost",
                        });
                        return;
                    }
                    item.discount = action.payload.discount;
                }
            };
            updateDiscount(state.selectedServices);
            updateDiscount(state.selectedMaterialResource);
        },
        applyPriceAdjustment: (state, action: PayloadAction<{ id: string; price: number }>) => {
            const updatePrice = (items: InvoiceCharge[]) => {
                const item = items.find((i) => i.id === action.payload.id);
                if (item) {
                    item.editablePrice = action.payload.price;
                }
            };
            updatePrice(state.selectedServices);
            updatePrice(state.selectedMaterialResource);
        },
        removeDiscount: (state, action: PayloadAction<string>) => {
            const removeDiscountFromItems = (items: InvoiceCharge[]) => {
                const item = items.find((i) => i.id === action.payload);
                if (item) {
                    item.discount = undefined;
                    item.editablePrice = undefined;
                }
            };
            removeDiscountFromItems(state.selectedServices);
            removeDiscountFromItems(state.selectedMaterialResource);
        },
        // Start date management for subscriptions
        updateStartDate: (
            state,
            action: PayloadAction<{ id: string; startDate: number | null }>,
        ) => {
            const service = state.selectedServices.find((s) => s.id === action.payload.id);
            if (service) {
                service.startDateUnix = action.payload.startDate ?? undefined;
            }
        },
        // Edit mode management
        setIsEditMode: (state, action: PayloadAction<boolean>) => {
            state.isEditMode = action.payload;
        },
        // Complete state reset
        resetDrawerState: () => initialStateInvoiceDrawer,
    },
    extraReducers: (builder) => {
        builder
            // Handle fetchInvoiceDetails
            .addCase(fetchInvoiceDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchInvoiceDetails.fulfilled, (state, action) => {
                state.invoiceDetailsFetched = true;
                // It's a regular invoice details object
                const invoiceDetails = action.payload as business_integrations.IInvoiceDetails;
                state.invoiceDetails = invoiceDetails;
                state.existingInvoiceId = invoiceDetails.id;
                // Convert invoice line items to InvoiceCharge format
                const convertedServices: InvoiceCharge[] = [];
                const convertedItems: InvoiceCharge[] = [];
                // Set patient data
                state.selectedPatient = {
                    accountPatientId: invoiceDetails.accountPatient.id,
                    name: invoiceDetails.accountPatient.name,
                    email: invoiceDetails.accountPatient.email,
                } as patients.IAccountPatient;
                // Set location editability based on appointment
                state.isLocationEditable = !invoiceDetails.appointmentId;
                // Process recurring payments
                const recurringPaymentsMap = new Map(
                    (invoiceDetails.recurringPayments || []).map((payment) => [
                        payment.careServiceId,
                        payment,
                    ]),
                );
                // Process line items
                invoiceDetails.lineItems.forEach((item) => {
                    let charge: InvoiceCharge = {
                        isPrepopulatedFromApptForms: false,
                        id:
                            item.careResourceType === ECareResourceType.SERVICE
                                ? `service_${item.careResourceId}`
                                : `item_${item.careResourceId}`,
                        careResourceId: item.careResourceId,
                        ...(item.existingInvoiceLineItemId && {
                            existingInvoiceLineItemId: item.existingInvoiceLineItemId,
                        }),
                        code: "",
                        description: item.careResourceName,
                        qty: item.quantity,
                        cost: item.amountCents / 100,
                        isService: item.careResourceType === ECareResourceType.SERVICE,
                        careResourceType: item.careResourceType,
                        discount: item.discountPercent === 0 ? undefined : item.discountPercent,
                        editablePrice: item.priceAdjustmentCents
                            ? (item.amountCents + item.priceAdjustmentCents) / 100
                            : undefined,
                        serviceRecurrenceType: item.serviceRecurrenceType,
                        taxRate: item.applyTax ? invoiceDetails.taxSettings?.salesTax : undefined,
                    };
                    // Add service or material resource details
                    if (item.serviceDetails) {
                        charge = {
                            ...charge,
                            code: item.serviceDetails.billingCode,
                        };
                    }
                    if (item.materialResourceDetails) {
                        charge = {
                            ...charge,
                            code: item.materialResourceDetails.sku,
                        };
                    }
                    // Add subscription details if this service has a recurring payment
                    const recurringPayment = recurringPaymentsMap.get(item.careResourceId);
                    if (recurringPayment) {
                        const startDate = dayjs(recurringPayment.startDateISO).tz("UTC");
                        charge = {
                            ...charge,
                            startDateUnix: startDate.valueOf(),
                            recurrenceFrequencyUnit: recurringPayment.recurrenceFrequencyUnit,
                            recurrenceFrequencyValue: recurringPayment.recurrenceFrequencyValue,
                            occurrences: recurringPayment.numberOfOccurrences,
                        };
                    }
                    // Sort into appropriate array
                    if (item.careResourceType === ECareResourceType.SERVICE) {
                        convertedServices.push(charge);
                    } else {
                        convertedItems.push(charge);
                    }
                });
                state.selectedServices = convertedServices;
                state.selectedMaterialResource = convertedItems;
                // Set edit mode based on invoice status
                const canEdit =
                    (invoiceDetails.isDraft ||
                        !invoiceDetails.statuses.includes(EPaymentRequestStatus.PAID)) &&
                    !invoiceDetails.statuses.includes(EPaymentRequestStatus.VOID);
                state.isVoid = invoiceDetails.statuses.includes(EPaymentRequestStatus.VOID);
                state.isRefunded = invoiceDetails.statuses.includes(EPaymentRequestStatus.REFUNDED);
                state.isReadOnly = !canEdit;
                state.isEditMode = false;
            })
            .addCase(fetchInvoiceDetails.rejected, (state, action) => {
                state.error = action.payload || "Failed to fetch invoice details";
                state.invoiceDetailsFetched = true;
            })
            .addCase(fetchAppointmentInvoiceContext.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAppointmentInvoiceContext.fulfilled, (state, action) => {
                state.invoiceDetailsFetched = true;
                const data = action.payload;
                // Check which type of response we received
                if (data.isInvoiceBuildingContext) {
                    // It's an invoice building context
                    const buildingContext = data as business_integrations.IInvoiceBuildingContext;
                    state.invoiceBuildingContext = buildingContext;
                    // Set patient data
                    state.selectedPatient = {
                        accountPatientId: buildingContext.accountPatientId,
                        name: buildingContext.accountPatientName,
                        email: buildingContext.accountPatientEmail,
                    } as patients.IAccountPatient;
                    // Set location editability
                    state.isLocationEditable = false;
                    state.isReadOnly = false;
                    state.isEditMode = true;
                    // Process prepopulated services from appointment forms
                    const prepopulatedServices: InvoiceCharge[] = (buildingContext.lineItems || [])
                        .filter((item) => item.careResourceType === ECareResourceType.SERVICE)
                        .map((item) => {
                            return {
                                isPrepopulatedFromApptForms: true,
                                id: item.careResourceId.toString(),
                                careResourceId: item.careResourceId,
                                code: item.serviceDetails?.billingCode || "",
                                description: item.careResourceName,
                                qty: item.quantity,
                                cost: item.amountCents / 100,
                                isService: true,
                                careResourceType: ECareResourceType.SERVICE,
                                startDateUnix: new Date().getTime(),
                                serviceRecurrenceType: item.serviceRecurrenceType,
                                recurrenceFrequencyUnit:
                                    item.serviceDetails?.recurrenceFrequencyUnit,
                                recurrenceFrequencyValue:
                                    item.serviceDetails?.recurrenceFrequencyValue,
                                occurrences: item.serviceDetails?.occurrences,
                                taxRate: item.applyTax
                                    ? state.invoiceDetails?.taxSettings?.salesTax
                                    : undefined,
                            };
                        });

                    if (prepopulatedServices.length) {
                        state.selectedServices = prepopulatedServices;
                    }
                } else {
                    // It's an invoice details object
                    const invoiceDetails = data as business_integrations.IInvoiceDetails;
                    state.invoiceDetails = invoiceDetails;
                    state.existingInvoiceId = invoiceDetails.id;
                    // Convert invoice line items to InvoiceCharge format
                    const convertedServices: InvoiceCharge[] = [];
                    const convertedItems: InvoiceCharge[] = [];
                    // Set patient data
                    state.selectedPatient = {
                        accountPatientId: invoiceDetails.accountPatient.id,
                        name: invoiceDetails.accountPatient.name,
                        email: invoiceDetails.accountPatient.email,
                    } as patients.IAccountPatient;
                    // Set location editability based on appointment
                    state.isLocationEditable = !invoiceDetails.appointmentId;
                    // Process recurring payments
                    const recurringPaymentsMap = new Map(
                        (invoiceDetails.recurringPayments || []).map((payment) => [
                            payment.careServiceId,
                            payment,
                        ]),
                    );
                    // Process line items
                    invoiceDetails.lineItems.forEach((item) => {
                        let charge: InvoiceCharge = {
                            isPrepopulatedFromApptForms: false,
                            id:
                                item.careResourceType === ECareResourceType.SERVICE
                                    ? `service_${item.careResourceId}`
                                    : `item_${item.careResourceId}`,
                            careResourceId: item.careResourceId,
                            ...(item.existingInvoiceLineItemId && {
                                existingInvoiceLineItemId: item.existingInvoiceLineItemId,
                            }),
                            code: "",
                            description: item.careResourceName,
                            qty: item.quantity,
                            cost: item.amountCents / 100,
                            isService: item.careResourceType === ECareResourceType.SERVICE,
                            careResourceType: item.careResourceType,
                            discount: item.discountPercent === 0 ? undefined : item.discountPercent,
                            editablePrice: item.priceAdjustmentCents
                                ? (item.amountCents + item.priceAdjustmentCents) / 100
                                : undefined,
                            serviceRecurrenceType: item.serviceRecurrenceType,
                            taxRate: item.applyTax
                                ? invoiceDetails.taxSettings?.salesTax
                                : undefined,
                        };
                        // Add service or material resource details
                        if (item.serviceDetails) {
                            charge = {
                                ...charge,
                                code: item.serviceDetails.billingCode,
                            };
                        }
                        if (item.materialResourceDetails) {
                            charge = {
                                ...charge,
                                code: item.materialResourceDetails.sku,
                            };
                        }
                        // Add subscription details if this service has a recurring payment
                        const recurringPayment = recurringPaymentsMap.get(item.careResourceId);
                        if (recurringPayment) {
                            const startDate = dayjs(recurringPayment.startDateISO).tz("UTC");
                            charge = {
                                ...charge,
                                startDateUnix: startDate.valueOf(),
                                recurrenceFrequencyUnit: recurringPayment.recurrenceFrequencyUnit,
                                recurrenceFrequencyValue: recurringPayment.recurrenceFrequencyValue,
                                occurrences: recurringPayment.numberOfOccurrences,
                            };
                        }
                        // Sort into appropriate array
                        if (item.careResourceType === ECareResourceType.SERVICE) {
                            convertedServices.push(charge);
                        } else {
                            convertedItems.push(charge);
                        }
                    });
                    state.selectedServices = convertedServices;
                    state.selectedMaterialResource = convertedItems;
                    // Set edit mode based on invoice status
                    const canEdit =
                        (invoiceDetails.isDraft ||
                            !invoiceDetails.statuses.includes(EPaymentRequestStatus.PAID)) &&
                        !invoiceDetails.statuses.includes(EPaymentRequestStatus.VOID);

                    state.isReadOnly = !canEdit;
                    state.isEditMode = false;
                }
            })
            .addCase(fetchAppointmentInvoiceContext.rejected, (state, action) => {
                state.error = action.payload || "Failed to fetch appointment invoice context";
                state.invoiceDetailsFetched = true;
            })
            .addCase(fetchChargableResources.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchChargableResources.fulfilled, (state, action) => {
                state.chargableResources = action.payload;
                state.chargeableResourcesFetched = true;
                // Transform material resources into InvoiceCharge format
                state.materialResourceChargeCatalog = action.payload.materialResources.map(
                    (item) => ({
                        isPrepopulatedFromApptForms: false,
                        id: `item_${item.id}`,
                        careResourceId: item.id,
                        code: item.materialResourceDetails?.sku || "",
                        description: item.name,
                        qty: 1,
                        cost: item.materialResourceDetails?.billable
                            ? item.defaultChargeAmountCents / 100
                            : null,
                        isService: false,
                        careResourceType: item.resourceType,
                        taxRate: item.applySalesTax
                            ? state.invoiceDetails?.taxSettings?.salesTax
                            : undefined,
                    }),
                );
                // Transform services into InvoiceCharge format
                state.serviceChargeCatalog = action.payload.services.map((service) => ({
                    isPrepopulatedFromApptForms: false,
                    id: `service_${service.id}`,
                    careResourceId: service.id,
                    code: service.serviceDetails?.billingCode || "",
                    description: service.name,
                    qty: 1,
                    cost: service.defaultChargeAmountCents / 100,
                    isService: true,
                    careResourceType: service.resourceType,
                    serviceRecurrenceType: service.serviceDetails?.recurrenceType,
                    recurrenceFrequencyUnit: service.serviceDetails?.recurrenceFrequencyUnit,
                    recurrenceFrequencyValue: service.serviceDetails?.recurrenceFrequencyValue,
                    occurrences: service.serviceDetails?.occurrences,
                    isBillableToInsurance: service.serviceDetails?.isBillableToInsurance,
                    taxRate: service.applySalesTax
                        ? state.invoiceDetails?.taxSettings?.salesTax
                        : undefined,
                }));
                state.wholeCatalog = [
                    ...state.materialResourceChargeCatalog,
                    ...state.serviceChargeCatalog,
                ];
            })
            .addCase(fetchChargableResources.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to fetch chargable resources";
                state.chargeableResourcesFetched = true;
            })
            .addCase(loadInvoiceDrawer.pending, (state, action) => {
                const { show, invoiceId, appointmentId, onSaveDraft, onCheckout } = action.meta.arg;
                state.isLoading = true;
                state.error = null;
                state.show = show === undefined ? true : show; // default behavior is to show the drawer
                state.invoiceId = invoiceId;
                state.appointmentId = appointmentId;
                state.onSaveDraft = onSaveDraft;
                state.onCheckout = onCheckout;
                if (!invoiceId && !appointmentId) {
                    state.isEditMode = true;
                    state.isReadOnly = false;
                }
            })
            .addCase(loadInvoiceDrawer.fulfilled, (state) => {
                state.isLoading = false;
                // Data loading is handled by individual fetch thunks
            })
            .addCase(loadInvoiceDrawer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to open invoice drawer";
            })
            .addCase(submitInvoice.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(submitInvoice.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.isLoading = false;
                const { invoice, showPaymentMethodConfigTab } = action.payload;
                if (showPaymentMethodConfigTab) {
                    state.showPaymentMethodConfigTab = true;
                    return;
                }
                if (!invoice) {
                    state.error = "No invoice data received";
                    return;
                }
                state.invoiceDetails = invoice;
                state.existingInvoiceId = invoice.id;
                // Handle payment request notifications
                const mostRecentPaymentRequest = invoice.paymentRequests
                    ?.sort((a, b) => {
                        return dayjs(a.paymentDateISO).isAfter(dayjs(b.paymentDateISO)) ? -1 : 1;
                    })
                    .at(0);

                if (mostRecentPaymentRequest?.statuses.includes(EPaymentRequestStatus.FAILED)) {
                    notify({
                        level: "error",
                        title: "Payment Failed",
                        message: "Please retry payment",
                    });
                } else if (
                    mostRecentPaymentRequest?.statuses.includes(EPaymentRequestStatus.PAID)
                ) {
                    notify({
                        level: "success",
                        title: "Payment Successful",
                        message: "Payment processed successfully",
                    });
                }
                if (action.meta.arg.isDraft) {
                    if (state.onSaveDraft) state.onSaveDraft();
                    state.show = false;
                }
                // Otherwise, update with new invoice details
                state.existingInvoiceId = invoice.id;
                // Set edit mode based on invoice status
                const canEdit =
                    (invoice.isDraft || !invoice.statuses.includes(EPaymentRequestStatus.PAID)) &&
                    !invoice.statuses.includes(EPaymentRequestStatus.VOID);
                state.isReadOnly = !canEdit;
                state.isEditMode = false;
                if (state.onCheckout) state.onCheckout();
            })
            .addCase(submitInvoice.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to submit invoice";
            })
            // Handle retryPayment
            .addCase(retryPayment.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(retryPayment.fulfilled, (state) => {
                state.isSubmitting = false;
                state.isLoading = false;
                state.showPaymentMethodConfigTab = false;
                if (state.onCheckout) state.onCheckout();
                // clear out selected card id, terminal id so if the user tries to retry payment again, it doesn't use the old values
                state.selectedCardId = null;
                state.selectedTerminalId = null;
            })
            .addCase(retryPayment.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to retry payment";
            })
            // Handle refundInvoice
            .addCase(refundInvoice.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(refundInvoice.fulfilled, (state) => {
                state.isSubmitting = false;
                state.isLoading = false;
                if (state.onCheckout) state.onCheckout();
            })
            .addCase(refundInvoice.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to refund invoice";
            });
    },
});

export const {
    hideInvoiceDrawer,
    setIsEditMode,
    setSelectedPatient,
    setCollectionOption,
    setSelectedCardId,
    setSelectedTerminalId,
    setShowPaymentMethodConfigTab,
    addInvoiceLineItem,
    removeInvoiceLineItem,
    updateQuantity,
    applyDiscount,
    applyPriceAdjustment,
    removeDiscount,
    updateStartDate,
    resetDrawerState,
} = invoiceDrawerSlice.actions;

export default invoiceDrawerSlice.reducer;
